import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Custom hook to intercept link clicks in HTML content and use React Router navigation
 * instead of page reloads. This enables SPA behavior for dynamically rendered HTML.
 * 
 * @param {string|Array} htmlContent - The HTML content string or array of HTML strings
 * @param {Object} options - Optional configuration
 * @param {boolean} options.enabled - Whether the hook is enabled (default: true)
 * @param {Function} options.onExternalLink - Callback for external link clicks
 * @param {Function} options.onInternalLink - Callback for internal link clicks before navigation
 * 
 * @returns {React.RefObject} - Ref to attach to the container element
 * 
 * @example
 * const containerRef = useHtmlLinkNavigation(bodyHtml);
 * return <div ref={containerRef} dangerouslySetInnerHTML={{ __html: bodyHtml }} />;
 */
const useHtmlLinkNavigation = (htmlContent, options = {}) => {
  const { enabled = true, onExternalLink, onInternalLink } = options;
  const navigate = useNavigate();
  const containerRef = useRef(null);

  useEffect(() => {
    if (!enabled || !htmlContent) return;

    const container = containerRef.current;
    if (!container) return;

    const handleClick = (event) => {
      // Find the closest anchor tag
      const anchor = event.target.closest('a');
      if (!anchor) return;

      const href = anchor.getAttribute('href');
      if (!href) return;

      // Check if it's an external link or special link
      const isExternal = /^(https?:|mailto:|tel:|\/\/)/.test(href);
      const isAnchor = href.startsWith('#');
      const isMailto = href.startsWith('mailto:');
      const isTel = href.startsWith('tel:');

      // Allow default behavior for external links, anchors, mailto, and tel
      if (isExternal || isAnchor || isMailto || isTel) {
        if (onExternalLink) {
          onExternalLink(href, event);
        }
        return; // Allow default behavior
      }

      // Internal link - prevent default and use React Router
      event.preventDefault();
      event.stopPropagation();

      // Call callback if provided
      if (onInternalLink) {
        onInternalLink(href, event);
      }

      // Normalize the path (ensure it starts with /)
      const path = href.startsWith('/') ? href : `/${href}`;
      
      // Navigate using React Router
      navigate(path);
    };

    // Add event listener
    container.addEventListener('click', handleClick);

    // Cleanup
    return () => {
      container.removeEventListener('click', handleClick);
    };
  }, [navigate, htmlContent, enabled, onExternalLink, onInternalLink]);

  return containerRef;
};

export default useHtmlLinkNavigation;

